import React, { useCallback } from 'react';

import PropTypes from 'prop-types';
import { defineMessages } from 'react-intl';
import { createSelector } from 'reselect';

import { noop } from 'ui-components/default-props/noop';

import ListWithOverflow from '../../components/list-with-overflow';
import { emptyArray } from 'ui-components/default-props/empty';
import style from './filterview-list.module.less';

const defaultViewIndex = 0;
const CUSTOM_VIEW_ID = 'custom';
const DEFAULT_VIEW_ID = 'default';

const requiredRenderIndicesSelector = createSelector(
  (activeFilterViewIndex) => activeFilterViewIndex,
  (_, customFilterViewIndex) => customFilterViewIndex,
  (activeFilterViewIndex, customFilterViewIndex) => [defaultViewIndex, activeFilterViewIndex, customFilterViewIndex],
);

const messages = defineMessages({
  overFlowDisplayName: {
    description: 'filterViewList.overFlowDisplayName',
    defaultMessage: 'More Views',
    id: 'ptOb2T',
  },
});

const getContainerType = (filterViewId, CustomFilterViewContainer, DefaultFilterViewContainer, FilterViewContainer) => {
  if (filterViewId === CUSTOM_VIEW_ID) {
    return CustomFilterViewContainer;
  }
  if (filterViewId.includes(DEFAULT_VIEW_ID)) {
    return DefaultFilterViewContainer;
  }
  return FilterViewContainer;
};

const findFilterViewIndex = (filterViewId, filterViews) => {
  return filterViews.findIndex((view) => view.filterViewId === filterViewId);
};

const findActiveFilterViewIndex = (activeFilterViewId, filterViews) => {
  return findFilterViewIndex(activeFilterViewId, filterViews);
};

const findCustomFilterViewIndex = (filterViews) => {
  return findFilterViewIndex(CUSTOM_VIEW_ID, filterViews);
};

const FilterViewList = ({
  filterViews = emptyArray,
  activeFilterViewId,
  switchActiveFilterView = noop,
  openEditViewModal = noop,
  openSaveViewModal = noop,
  DefaultFilterViewContainer,
  FilterViewContainer,
  CustomFilterViewContainer,
}) => {
  const requiredRenderIndices = requiredRenderIndicesSelector(
    findActiveFilterViewIndex(activeFilterViewId, filterViews),
    findCustomFilterViewIndex(filterViews),
  );
  const filterViewComponents = filterViews.map((filterView, index) => {
    const Container = getContainerType(filterView.filterViewId, CustomFilterViewContainer, DefaultFilterViewContainer, FilterViewContainer);
    return (
      <Container
        key={index}
        count={filterView.filterCount}
        displayName={filterView.displayName}
        filterViewId={filterView.filterViewId}
        openEditViewModal={openEditViewModal}
        openSaveViewModal={openSaveViewModal}
      />
    );
  });

  const onOverflowMenuItemSelected = useCallback(
    (viewName) => {
      const selectedFilterView = filterViews.find((view) => view.displayName === viewName);
      switchActiveFilterView(selectedFilterView.filterViewId);
    },
    [filterViews, switchActiveFilterView],
  );

  return (
    <div className={style.container}>
      <ListWithOverflow
        componentOverflowDisplayPropName='displayName'
        componentsToRender={filterViewComponents}
        onOverflowMenuItemSelected={onOverflowMenuItemSelected}
        overflowMenuTranslation={messages.overFlowDisplayName}
        requiredRenderIndices={requiredRenderIndices}
      />
    </div>
  );
};

FilterViewList.propTypes = {
  filterViews: PropTypes.arrayOf(
    PropTypes.shape({
      filterViewId: PropTypes.string,
      filterCount: PropTypes.number,
      displayName: PropTypes.string,
    }),
  ),
  activeFilterViewId: PropTypes.string.isRequired,
  switchActiveFilterView: PropTypes.func,
  openEditViewModal: PropTypes.func,
  openSaveViewModal: PropTypes.func,
  DefaultFilterViewContainer: PropTypes.oneOfType([PropTypes.func, PropTypes.object]).isRequired,
  FilterViewContainer: PropTypes.oneOfType([PropTypes.func, PropTypes.object]).isRequired,
  CustomFilterViewContainer: PropTypes.oneOfType([PropTypes.func, PropTypes.object]).isRequired,
};

export default FilterViewList;
